import React, { useState, useEffect, useRef, useCallback } from 'react';
import { subscribeRoom } from '../shared/roomTx';
import {
  startChainAnswering,
  submitChainAnswer,
  proceedToNextTurn,
  continueAfterWrong,
  handleChainTimeout,
  showChainResult,
} from '../shared/chainRoomTx';
import { getRemainingTime, getPlayerBySlot, CHAIN_RESULT } from '../shared/chainLogic';
import {
  FL_ROOM_STATUS,
  CHAIN_PHASE,
  CHAIN_SETTINGS,
  normalizeChainAnswer,
} from '../shared/fiveLeague';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import VoiceInput, { isVoiceInputSupported } from '../components/VoiceInput';
import './ChainGame.css';

function ChainGame({ navigate, playerId, roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inputText, setInputText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showCorrectAnimation, setShowCorrectAnimation] = useState(false);
  const [showWrongAnimation, setShowWrongAnimation] = useState(false);
  const [lastCorrectAnswer, setLastCorrectAnswer] = useState(null);
  const [inputMode, setInputMode] = useState(() => isVoiceInputSupported() ? 'voice' : 'text');

  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // ルーム購読
  useEffect(() => {
    if (!roomId) {
      navigate('home');
      return;
    }

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      if (!data) {
        setError('ルームが見つかりません');
        return;
      }

      setRoom(data);

      // 結果画面へ
      if (data.status === FL_ROOM_STATUS.FINISHED) {
        navigate('chain-result', { roomId });
      }
    });

    return () => unsub();
  }, [roomId, navigate]);

  // タイマー更新
  useEffect(() => {
    if (!room?.game?.endsAt) return;

    const updateTimer = () => {
      const remaining = getRemainingTime(room.game.endsAt);
      setRemainingTime(remaining);

      // タイムアウト処理はサーバー側で行う
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 100);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [room?.game?.endsAt]);

  // フェーズ遷移の処理
  useEffect(() => {
    if (!room?.game) return;

    const { phase } = room.game;

    // 正解アニメーション後、次のターンへ
    if (phase === CHAIN_PHASE.CORRECT) {
      setShowCorrectAnimation(true);
      const timer = setTimeout(async () => {
        setShowCorrectAnimation(false);
        setInputText('');
        await proceedToNextTurn(roomId);
      }, 1500);
      return () => clearTimeout(timer);
    }

    // 不正解アニメーション後、継続
    if (phase === CHAIN_PHASE.WRONG) {
      setShowWrongAnimation(true);
      const timer = setTimeout(async () => {
        setShowWrongAnimation(false);
        setInputText('');
        await continueAfterWrong(roomId);
      }, 1500);
      return () => clearTimeout(timer);
    }

    // タイムアウト後、結果画面へ
    if (phase === CHAIN_PHASE.TIMEOUT) {
      const timer = setTimeout(async () => {
        await showChainResult(roomId);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // 全問正解後、結果画面へ
    if (phase === CHAIN_PHASE.COMPLETE) {
      const timer = setTimeout(async () => {
        await showChainResult(roomId);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [room?.game?.phase, roomId]);

  // 問題読み上げ後、回答開始
  const handleStartAnswering = useCallback(async () => {
    const result = await startChainAnswering(roomId);
    if (!result.ok) {
      console.error('Failed to start answering:', result.reason);
    }
  }, [roomId]);

  // 回答送信
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!inputText.trim() || submitting) return;

    const game = room?.game;
    if (!game || game.phase !== CHAIN_PHASE.ANSWERING) return;

    // 自分のターンか確認
    const currentPlayerId = room.playerOrder[game.currentPlayerSlot];
    if (currentPlayerId !== playerId) return;

    setSubmitting(true);

    try {
      const result = await submitChainAnswer(roomId, playerId, inputText.trim());
      if (result.ok) {
        if (result.result === CHAIN_RESULT.CORRECT) {
          setLastCorrectAnswer(result.matchedAnswer);
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // 入力欄にフォーカス
  useEffect(() => {
    if (room?.game?.phase === CHAIN_PHASE.ANSWERING && inputRef.current) {
      const currentPlayerId = room.playerOrder[room.game.currentPlayerSlot];
      if (currentPlayerId === playerId) {
        inputRef.current.focus();
      }
    }
  }, [room?.game?.phase, room?.game?.currentPlayerSlot, room?.playerOrder, playerId]);

  if (loading) {
    return (
      <div className="chain-game-container">
        <CyberBackground showParticles={true} />
        <div className="loading-spinner">読み込み中...</div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="chain-game-container">
        <CyberBackground showParticles={true} />
        <p className="error-message">{error || 'エラーが発生しました'}</p>
        <button className="btn-secondary" onClick={() => navigate('home')}>
          ホームに戻る
        </button>
      </div>
    );
  }

  const game = room.game;
  if (!game) {
    return (
      <div className="chain-game-container">
        <CyberBackground showParticles={true} />
        <p className="error-message">ゲームデータがありません</p>
      </div>
    );
  }

  const isMyTurn = room.playerOrder[game.currentPlayerSlot] === playerId;
  const currentPlayer = getPlayerBySlot(game.currentPlayerSlot, room.playerOrder, room.players);
  const controllers = room.players?.filter(p => p.role !== 'display') || [];

  // READING フェーズ
  if (game.phase === CHAIN_PHASE.READING) {
    return (
      <div className="chain-game-container animate-fade-in">
        <CyberBackground showParticles={true} />

        <div className="chain-game-content">
          <div className="chain-header">
            <NeonText color="pink" size="lg">⚡ アンサーチェーン</NeonText>
          </div>

          <GlowingBorder color="accent" className="question-box reading-phase">
            <p className="question-prompt">{game.prompt}</p>
          </GlowingBorder>

          <div className="reading-info">
            <p className="reading-text">問題を確認してください...</p>
            <CyberButton
              variant="primary"
              className="start-answering-btn"
              onClick={handleStartAnswering}
            >
              回答開始！
            </CyberButton>
          </div>
        </div>
      </div>
    );
  }

  // TIMEOUT フェーズ
  if (game.phase === CHAIN_PHASE.TIMEOUT) {
    return (
      <div className="chain-game-container animate-fade-in">
        <CyberBackground showParticles={true} />

        <div className="chain-game-content">
          <div className="timeout-screen">
            <div className="timeout-icon">💥</div>
            <NeonText color="pink" size="xl">TIME UP!</NeonText>
            <p className="timeout-result">
              {game.correctCount} / {game.answerCount} 正解
            </p>
          </div>
        </div>
      </div>
    );
  }

  // COMPLETE フェーズ
  if (game.phase === CHAIN_PHASE.COMPLETE) {
    return (
      <div className="chain-game-container animate-fade-in">
        <CyberBackground showParticles={true} />

        <div className="chain-game-content">
          <div className="complete-screen">
            <div className="complete-icon">🎉</div>
            <NeonText color="accent" size="xl">PERFECT!</NeonText>
            <p className="complete-result">
              {game.answerCount}問 全問正解！
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ANSWERING / CORRECT / WRONG フェーズ
  return (
    <div className="chain-game-container animate-fade-in">
      <CyberBackground showParticles={true} />

      {/* 正解アニメーション */}
      {showCorrectAnimation && (
        <div className="answer-overlay correct">
          <div className="answer-result-icon">⭕</div>
          <NeonText color="accent" size="xl">正解！</NeonText>
          {lastCorrectAnswer && (
            <p className="matched-answer">{lastCorrectAnswer.text}</p>
          )}
        </div>
      )}

      {/* 不正解アニメーション */}
      {showWrongAnimation && (
        <div className="answer-overlay wrong">
          <div className="answer-result-icon">❌</div>
          <NeonText color="pink" size="xl">
            {game.lastWrongReason === 'duplicate' ? 'かぶり！' : 'ブブー'}
          </NeonText>
        </div>
      )}

      <div className="chain-game-content">
        {/* ヘッダー: タイマーと進捗 */}
        <div className="chain-header">
          <div className="chain-timer">
            <span className={`timer-value ${remainingTime <= 10 ? 'warning' : ''} ${remainingTime <= 5 ? 'critical' : ''}`}>
              {remainingTime}
            </span>
            <span className="timer-label">秒</span>
          </div>
          <div className="chain-progress">
            <span className="progress-current">{game.correctCount}</span>
            <span className="progress-separator">/</span>
            <span className="progress-total">{game.answerCount}</span>
          </div>
        </div>

        {/* 問題 */}
        <GlowingBorder color="purple" className="question-box">
          <p className="question-prompt">{game.prompt}</p>
        </GlowingBorder>

        {/* 回答済みリスト */}
        <div className="submitted-answers">
          {game.submittedAnswers?.map((answer, i) => (
            <div key={i} className="submitted-answer">
              <span className="answer-number">{i + 1}</span>
              <span className="answer-text">{answer.text}</span>
            </div>
          ))}
        </div>

        {/* 現在のプレイヤー */}
        <div className={`current-player-section ${isMyTurn ? 'my-turn' : ''}`}>
          <GlowingBorder
            color={isMyTurn ? 'accent' : 'blue'}
            className="current-player-card"
          >
            <span className="current-player-label">
              {isMyTurn ? 'あなたの番！' : '回答中...'}
            </span>
            <span className="current-player-name">
              {currentPlayer?.nickname || '???'}
            </span>
          </GlowingBorder>
        </div>

        {/* 入力欄 (自分のターンのみ) */}
        {isMyTurn && game.phase === CHAIN_PHASE.ANSWERING && (
          <div className="input-section">
            {/* 入力モード切替 */}
            {isVoiceInputSupported() && (
              <div className="input-mode-toggle">
                <button
                  type="button"
                  className={`mode-btn ${inputMode === 'voice' ? 'active' : ''}`}
                  onClick={() => setInputMode('voice')}
                >
                  🎤 音声
                </button>
                <button
                  type="button"
                  className={`mode-btn ${inputMode === 'text' ? 'active' : ''}`}
                  onClick={() => setInputMode('text')}
                >
                  ⌨️ テキスト
                </button>
              </div>
            )}

            {/* 音声入力モード */}
            {inputMode === 'voice' && isVoiceInputSupported() ? (
              <VoiceInput
                onResult={(text) => {
                  setInputText(text);
                  // 自動送信
                  setTimeout(() => {
                    if (text.trim()) {
                      handleSubmit();
                    }
                  }, 100);
                }}
                onInterim={(text) => setInputText(text)}
                disabled={submitting}
                autoStart={true}
                placeholder="回答を話してください..."
              />
            ) : (
              /* テキスト入力モード */
              <form className="answer-form" onSubmit={handleSubmit}>
                <input
                  ref={inputRef}
                  type="text"
                  className="answer-input cyber-input"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value.slice(0, CHAIN_SETTINGS.MAX_TEXT_LENGTH))}
                  placeholder="回答を入力..."
                  disabled={submitting}
                  autoComplete="off"
                />
                <CyberButton
                  type="submit"
                  variant="accent"
                  className="submit-answer-btn"
                  disabled={!inputText.trim() || submitting}
                >
                  {submitting ? '...' : '回答'}
                </CyberButton>
              </form>
            )}
          </div>
        )}

        {/* 音声モードの回答確認ボタン */}
        {isMyTurn && game.phase === CHAIN_PHASE.ANSWERING && inputMode === 'voice' && inputText && (
          <CyberButton
            variant="accent"
            className="submit-answer-btn voice-submit-btn"
            onClick={handleSubmit}
            disabled={!inputText.trim() || submitting}
          >
            {submitting ? '送信中...' : `「${inputText}」で回答`}
          </CyberButton>
        )}

        {/* 他のプレイヤーの番 */}
        {!isMyTurn && game.phase === CHAIN_PHASE.ANSWERING && (
          <div className="waiting-message">
            <p>{currentPlayer?.nickname}さんの回答を待っています...</p>
          </div>
        )}

        {/* プレイヤー順リスト */}
        <div className="player-slots">
          {controllers.map((player, i) => {
            const slot = room.playerOrder.indexOf(player.playerId);
            const isCurrent = slot === game.currentPlayerSlot;
            return (
              <div
                key={player.playerId}
                className={`player-slot ${isCurrent ? 'current' : ''} ${player.playerId === playerId ? 'me' : ''}`}
              >
                <span className="slot-name">{player.nickname}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ChainGame;
